import { useEffect, useState, useRef, useCallback } from 'react';
import api from '../lib/api';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Hook to manage Web Push notification subscription.
 *
 * Usage:
 *   const { supported, permission, subscribed, subscribe, unsubscribe } = usePushNotifications();
 *
 * Call subscribe() after the user grants permission (e.g. on button click).
 * The hook automatically registers the SW and sends the subscription to the backend.
 */
export default function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const swRegRef = useRef(null);

  // Check support on mount
  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(ok);
    if (ok) setPermission(Notification.permission);
  }, []);

  // Check existing subscription
  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready.then(reg => {
      swRegRef.current = reg;
      return reg.pushManager.getSubscription();
    }).then(sub => {
      setSubscribed(!!sub);
    }).catch(() => {});
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) return false;
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      // Get SW registration
      const reg = swRegRef.current || await navigator.serviceWorker.ready;
      swRegRef.current = reg;

      // Subscribe
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send to backend
      const subJSON = sub.toJSON();
      await api.post('/push/subscribe', {
        subscription: {
          endpoint: subJSON.endpoint,
          keys: subJSON.keys
        }
      });

      setSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push subscribe failed:', err);
      return false;
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = swRegRef.current || await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      await api.post('/push/unsubscribe');
      setSubscribed(false);
    } catch {}
  }, []);

  return { supported, permission, subscribed, subscribe, unsubscribe };
}
